import {
  select,
  scaleLinear,
  scaleBand,
  axisBottom,
  axisLeft,
  line,
  max,
} from 'd3';
import {
  splitAndTrim,
  parseValue,
  fetchChartData,
  setupHoverHighlight,
  calculateLegendWidths,
  calculateLegendRows,
} from './chart-utils';

/**
 * Initializes annotated charts on the page
 */
export function initAnnotatedChart() {
  const charts = document.querySelectorAll(
    '.annotated-chart-wrapper .chart-inner',
  );

  if (charts.length === 0) {
    return;
  }

  for (const chart of charts) {
    if (chart instanceof HTMLElement) {
      renderAnnotatedChart(chart);
    }
  }
}

/**
 * Renders a single annotated chart
 * @param {HTMLElement} chartElement - The chart container element
 */
function renderAnnotatedChart(chartElement) {
  const file = chartElement.getAttribute('data-file');
  const xColumn = chartElement.getAttribute('data-x-column') || 'Date';
  const barColumns = chartElement.getAttribute('data-bar-columns') || '';
  const lineColumn = chartElement.getAttribute('data-line-column') || '';
  const labelColumn = chartElement.getAttribute('data-label-column') || '';
  const colors = chartElement.getAttribute('data-colors') || '';
  const yFormat = chartElement.getAttribute('data-y-format') || 'currency';

  if (!file) {
    return;
  }

  const barColumnsArray = splitAndTrim(barColumns);
  const colorArray = splitAndTrim(colors);

  fetchChartData(chartElement, file, '.annotated-chart-wrapper', (data) => {
    renderChart(
      chartElement,
      data,
      xColumn,
      barColumnsArray,
      lineColumn,
      labelColumn,
      colorArray,
      yFormat,
    );
  });
}

/**
 * Renders the chart using D3
 * @param {HTMLElement} container - The chart container
 * @param {Array} data - Parsed CSV data
 * @param {string} xColumn - X column name
 * @param {Array<string>} barColumns - Bar column names
 * @param {string} lineColumn - Line column name
 * @param {string} labelColumn - Label column name
 * @param {Array<string>} colors - Color array
 * @param {string} yFormat - Y-axis format type
 */
function renderChart(
  container,
  data,
  xColumn,
  barColumns,
  lineColumn,
  labelColumn,
  colors,
  yFormat,
) {
  // Clear container
  container.innerHTML = '';

  // Set up dimensions
  const margin = { top: 0, right: 20, bottom: 60, left: 60 };
  const width = container.clientWidth - margin.left - margin.right;
  const height = container.clientHeight - margin.top - margin.bottom;
  const totalWidth = width + margin.left + margin.right;
  const totalHeight = height + margin.top + margin.bottom;

  // Create SVG with viewBox for responsive scaling
  const svg = select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .style('width', '100%')
    .style('height', 'auto')
    .style('opacity', 0)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Fade in the chart
  select(container)
    .select('svg')
    .transition()
    .duration(600)
    .style('opacity', 1);

  // Process data with index-based approach
  const yearsWithLabels = new Set();
  const processedData = data.map((d, i) => {
    const year = parseInt(d[xColumn], 10);
    const showLabel = !yearsWithLabels.has(year);
    if (showLabel) {
      yearsWithLabels.add(year);
    }
    return {
      index: i,
      year,
      showLabel,
      ...barColumns.reduce((acc, col) => {
        acc[col] = parseValue(d[col]);
        return acc;
      }, {}),
      lineValue: parseValue(d[lineColumn]),
      label: d[labelColumn] || '',
    };
  });

  // X scale based on index
  const xScale = scaleBand()
    .domain(processedData.map((d) => String(d.index)))
    .range([0, width])
    .padding(0.1);

  // Y scale
  const maxBarValue = max(processedData, (d) =>
    Math.max(...barColumns.map((col) => d[col])),
  );
  const maxLineValue = max(processedData, (d) => d.lineValue);
  const yScale = scaleLinear()
    .domain([0, Math.max(maxBarValue || 0, maxLineValue || 0) * 1.1])
    .range([height, 0]);

  // X axis
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(
      axisBottom(xScale)
        .tickValues(
          processedData.filter((d) => d.showLabel).map((d) => String(d.index)),
        )
        .tickFormat((i) => {
          const d = processedData.find((item) => String(item.index) === i);
          return d ? String(d.year) : '';
        })
        .tickSize(0)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').attr('stroke', '#444'))
    .call((g) =>
      g
        .selectAll('text')
        .style('text-anchor', 'end')
        .style('fill', '#1D2328')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-65)'),
    );

  // Y axis
  svg
    .append('g')
    .attr('class', 'y-axis')
    .call(
      axisLeft(yScale)
        .tickFormat((d) => {
          if (yFormat === 'currency') {
            return `$${(Number(d) / 1000).toFixed(1)}k`;
          }
          return Number(d).toLocaleString();
        })
        .tickSize(-width)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').remove())
    .call((g) => g.selectAll('.tick line').attr('stroke', '#e0e0e0'))
    .call((g) =>
      g.selectAll('text').style('fill', '#999').style('font-size', '14px'),
    );

  // Draw bars side-by-side
  const barWidth = xScale.bandwidth() / barColumns.length;
  const barGroups = [];

  for (let i = 0; i < barColumns.length; i++) {
    const column = barColumns[i];
    const bars = svg
      .selectAll(`.bar-${i}`)
      .data(processedData)
      .enter()
      .append('rect')
      .attr('class', `bar-${i}`)
      .attr('data-series', column)
      .attr('x', (d) => (xScale(String(d.index)) || 0) + i * barWidth)
      .attr('y', (d) => yScale(d[column]))
      .attr('height', (d) => height - yScale(d[column]))
      .attr('width', barWidth)
      .attr('fill', colors[i] || '#264C68')
      .style('cursor', 'pointer');

    barGroups.push({ series: column, element: bars });
  }

  // Draw line
  const linePath = line()
    .x((d) => (xScale(String(d.index)) || 0) + xScale.bandwidth() / 2)
    .y((d) => yScale(d.lineValue));

  const lineSeries = svg
    .append('path')
    .datum(processedData)
    .attr('fill', 'none')
    .attr('stroke', colors[2] || '#1D2328')
    .attr('stroke-width', 2)
    .attr('data-series', lineColumn)
    .attr('d', (d) => linePath(d))
    .style('cursor', 'pointer');

  // Add annotations for labeled points
  processedData.forEach((d) => {
    if (d.label) {
      const x = (xScale(String(d.index)) || 0) + xScale.bandwidth() / 2;
      const y = yScale(d.lineValue);

      // Create annotation group
      const annotation = svg.append('g').attr('class', 'annotation');

      // Draw circle
      annotation
        .append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 4)
        .attr('fill', colors[2] || '#1D2328');

      // Draw label with background (label already includes $ if present)
      const labelText = d.label;

      // Add background first
      const tempText = annotation
        .append('text')
        .attr('x', x)
        .attr('y', y - 12)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '700')
        .style('visibility', 'hidden')
        .text(labelText);

      const bbox = tempText.node()?.getBBox();
      tempText.remove();

      if (bbox) {
        annotation
          .append('rect')
          .attr('x', bbox.x - 4)
          .attr('y', bbox.y - 2)
          .attr('width', bbox.width + 8)
          .attr('height', bbox.height + 4)
          .attr('fill', '#f5f5f5')
          .attr('rx', 3);
      }

      // Add text on top
      annotation
        .append('text')
        .attr('x', x)
        .attr('y', y - 12)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '700')
        .style('fill', '#1D2328')
        .text(labelText);
    }
  });

  // Create legend
  const legendItems = [
    ...barColumns.map((col, i) => ({
      label: col,
      color: colors[i] || '#264C68',
      type: 'rect',
    })),
    { label: lineColumn, color: colors[2] || '#1D2328', type: 'line' },
  ];

  // Calculate legend widths and rows for wrapping
  const legendWidths = calculateLegendWidths(svg, legendItems);
  const rows = calculateLegendRows(legendWidths, width);

  const rowHeight = 20;
  const legendHeight = rows.length * rowHeight;

  // Update SVG height to accommodate legend
  const newTotalHeight = height + margin.top + margin.bottom + legendHeight;
  select(container)
    .select('svg')
    .attr('viewBox', `0 0 ${totalWidth} ${newTotalHeight}`);

  // Create actual legend with multiple rows
  const legend = svg
    .append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(0,${height + 60})`);

  const legendElements = [];

  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    const row = rows[rowIdx];
    const rowWidth = row.reduce((sum, i) => sum + legendWidths[i], 0);
    const rowX = (width - rowWidth) / 2;

    let currentX = 0;

    for (const i of row) {
      const item = legendItems[i];
      const legendItem = legend
        .append('g')
        .attr('class', 'legend-item')
        .attr('data-series', item.label)
        .attr(
          'transform',
          `translate(${rowX + currentX},${rowIdx * rowHeight})`,
        )
        .style('cursor', 'pointer');

      if (item.type === 'rect') {
        legendItem
          .append('rect')
          .attr('x', 0)
          .attr('y', -6)
          .attr('width', 12)
          .attr('height', 12)
          .attr('fill', item.color);
      } else {
        legendItem
          .append('line')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr('x2', 12)
          .attr('y2', 0)
          .attr('stroke', item.color)
          .attr('stroke-width', 2);
      }

      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '11px')
        .style('fill', '#444')
        .style(
          'font-family',
          '"Roboto", "Helvetica Neue", "HelveticaNeue", "Helvetica", "Arial", sans-serif',
        )
        .text(item.label);

      legendElements.push({ label: item.label, element: legendItem });
      currentX += legendWidths[i];
    }
  }

  // Setup hover highlighting
  setupHoverHighlight(svg, legendElements, [
    ...barGroups,
    { series: lineColumn, element: lineSeries },
  ]);
}
